import { exec, getApiToken } from '@src/apis/reware/utils';
import type { DownloadLink } from './types';

interface BaseArgs {
  partnerCode: string;
  token: string;
  entry: string;
}

interface BooleanResultExecResponse {
  restUser: {
    partner: Record<string, { result: boolean } | null>;
  };
}

const runBooleanMutation = async <TVariables extends { token: string, partnerCode: string }>(
  field: string,
  query: string,
  variables: TVariables
): Promise<boolean> => {
  const data = await exec<TVariables, BooleanResultExecResponse>(query, { variables });
  return Boolean(data.restUser.partner[field]?.result);
};

/** Creates a sub-folder within a partner SFTP folder entry. `path` is the full path of the new folder (parent + name). */
export const createSftpSubfolder = async (
  { partnerCode, token, entry, path }: BaseArgs & { path: string }
): Promise<boolean> => {
  const apiToken = await getApiToken(token);
  const query = `
    mutation createSftpSubfolder(
      $token: String!
      $partnerCode: String!
      $entry: String!
      $path: String!
    ) {
      restUser(token: $token) {
        partner(code: $partnerCode) {
          createSftpSubfolder(entry: $entry, path: $path) {
            result
          }
        }
      }
    }
  `;
  return runBooleanMutation('createSftpSubfolder', query, { token: apiToken, partnerCode, entry, path });
};

/** Deletes a sub-folder (and everything under it). `path` is the full path of the folder being deleted. */
export const deleteSftpSubfolder = async (
  { partnerCode, token, entry, path }: BaseArgs & { path: string }
): Promise<boolean> => {
  const apiToken = await getApiToken(token);
  const query = `
    mutation deleteSftpSubfolder(
      $token: String!
      $partnerCode: String!
      $entry: String!
      $path: String!
    ) {
      restUser(token: $token) {
        partner(code: $partnerCode) {
          deleteSftpSubfolder(entry: $entry, path: $path) {
            result
          }
        }
      }
    }
  `;
  return runBooleanMutation('deleteSftpSubfolder', query, { token: apiToken, partnerCode, entry, path });
};

/** Renames a sub-folder. `path` is the full path of the folder being renamed; `newName` is just the new leaf name. */
export const renameSftpSubfolder = async (
  { partnerCode, token, entry, path, newName }: BaseArgs & { path: string, newName: string }
): Promise<boolean> => {
  const apiToken = await getApiToken(token);
  const query = `
    mutation renameSftpSubfolder(
      $token: String!
      $partnerCode: String!
      $entry: String!
      $path: String!
      $newName: String!
    ) {
      restUser(token: $token) {
        partner(code: $partnerCode) {
          renameSftpSubfolder(entry: $entry, path: $path, newName: $newName) {
            result
          }
        }
      }
    }
  `;
  return runBooleanMutation('renameSftpSubfolder', query, { token: apiToken, partnerCode, entry, path, newName });
};

/** Deletes a file. `path` is the parent folder (omit at entry root); `filename` is separate. */
export const deleteSftpFile = async (
  { partnerCode, token, entry, path, filename }: BaseArgs & { path?: string, filename: string }
): Promise<boolean> => {
  const apiToken = await getApiToken(token);
  const query = `
    mutation deleteSftpFile(
      $token: String!
      $partnerCode: String!
      $entry: String!
      $path: String
      $filename: String!
    ) {
      restUser(token: $token) {
        partner(code: $partnerCode) {
          deleteSftpFile(entry: $entry, path: $path, filename: $filename) {
            result
          }
        }
      }
    }
  `;
  return runBooleanMutation('deleteSftpFile', query, { token: apiToken, partnerCode, entry, path, filename });
};

/** Renames a file. `path` is the parent folder (omit at entry root). */
export const renameSftpFile = async (
  { partnerCode, token, entry, path, filename, newFilename }: BaseArgs & { path?: string, filename: string, newFilename: string }
): Promise<boolean> => {
  const apiToken = await getApiToken(token);
  const query = `
    mutation renameSftpFile(
      $token: String!
      $partnerCode: String!
      $entry: String!
      $path: String
      $filename: String!
      $newFilename: String!
    ) {
      restUser(token: $token) {
        partner(code: $partnerCode) {
          renameSftpFile(entry: $entry, path: $path, filename: $filename, newFilename: $newFilename) {
            result
          }
        }
      }
    }
  `;
  return runBooleanMutation('renameSftpFile', query, { token: apiToken, partnerCode, entry, path, filename, newFilename });
};

/** Moves a file to `destinationPath` within the same entry. `path` is the file's current parent (omit at root). */
export const moveSftpFile = async (
  { partnerCode, token, entry, path, filename, destinationPath }: BaseArgs & { path?: string, filename: string, destinationPath?: string }
): Promise<boolean> => {
  const apiToken = await getApiToken(token);
  const query = `
    mutation moveSftpFile(
      $token: String!
      $partnerCode: String!
      $entry: String!
      $path: String
      $filename: String!
      $destinationPath: String
    ) {
      restUser(token: $token) {
        partner(code: $partnerCode) {
          moveSftpFile(entry: $entry, path: $path, filename: $filename, destinationPath: $destinationPath) {
            result
          }
        }
      }
    }
  `;
  return runBooleanMutation('moveSftpFile', query, { token: apiToken, partnerCode, entry, path, filename, destinationPath });
};

/** Moves a sub-folder (and its contents) to `destinationPath` within the same entry. `path` is the folder's current full path. */
export const moveSftpSubfolder = async (
  { partnerCode, token, entry, path, destinationPath }: BaseArgs & { path: string, destinationPath: string }
): Promise<boolean> => {
  const apiToken = await getApiToken(token);
  const query = `
    mutation moveSftpSubfolder(
      $token: String!
      $partnerCode: String!
      $entry: String!
      $path: String!
      $destinationPath: String!
    ) {
      restUser(token: $token) {
        partner(code: $partnerCode) {
          moveSftpSubfolder(entry: $entry, path: $path, destinationPath: $destinationPath) {
            result
          }
        }
      }
    }
  `;
  return runBooleanMutation('moveSftpSubfolder', query, { token: apiToken, partnerCode, entry, path, destinationPath });
};

interface GetSftpFileUploadUrlExecResponse {
  restUser: {
    partner: {
      getSftpFileUploadUrl: { uploadUrl: string };
    };
  };
}

/** Generates a presigned S3 upload URL for a file within a partner SFTP folder entry. */
export const getSftpFileUploadUrl = async (
  { partnerCode, token, entry, path, filename, contentType }: BaseArgs & { path?: string, filename: string, contentType?: string }
): Promise<string> => {
  const apiToken = await getApiToken(token);
  const query = `
    mutation getSftpFileUploadUrl(
      $token: String!
      $partnerCode: String!
      $entry: String!
      $path: String
      $filename: String!
      $contentType: String
    ) {
      restUser(token: $token) {
        partner(code: $partnerCode) {
          getSftpFileUploadUrl(entry: $entry, path: $path, filename: $filename, contentType: $contentType) {
            uploadUrl
          }
        }
      }
    }
  `;
  const data = await exec<Record<string, unknown>, GetSftpFileUploadUrlExecResponse>(query, {
    variables: { token: apiToken, partnerCode, entry, path, filename, contentType },
  });
  return data.restUser.partner.getSftpFileUploadUrl.uploadUrl;
};

interface GetSftpFileDownloadUrlsExecResponse {
  restUser: {
    partner: {
      getSftpFileDownloadUrls: DownloadLink[];
    };
  };
}

/** Generates presigned S3 download URLs for one or more files within a partner SFTP folder entry. */
export const getSftpFileDownloadUrls = async (
  { partnerCode, token, entry, path, filenames }: BaseArgs & { path?: string, filenames: string[] }
): Promise<DownloadLink[]> => {
  const apiToken = await getApiToken(token);
  const query = `
    mutation getSftpFileDownloadUrls(
      $token: String!
      $partnerCode: String!
      $entry: String!
      $path: String
      $filenames: [String!]!
    ) {
      restUser(token: $token) {
        partner(code: $partnerCode) {
          getSftpFileDownloadUrls(entry: $entry, path: $path, filenames: $filenames) {
            name
            url
          }
        }
      }
    }
  `;
  const data = await exec<Record<string, unknown>, GetSftpFileDownloadUrlsExecResponse>(query, {
    variables: { token: apiToken, partnerCode, entry, path, filenames },
  });
  return data.restUser.partner.getSftpFileDownloadUrls;
};
